import { ChevronDown } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "~/components/ui/collapsible";
import { cn } from "~/lib/utils";
import { shortModelName } from "./primitives/format";

/** Human labels for the `judge_config` keys, models and rubric text alike. */
const CONFIG_KEY_LABELS: Record<string, string> = {
  figure_judge_model: "Figure judge",
  text_response_judge_model: "Text response judge",
  figure_judging_instructions: "Figure rubric",
  text_response_judging_instructions: "Text response rubric",
  skill_content_header: "Skill header",
};

/** The `judge_config` keys that name a model, so the summary truncates them the
 * way the runs table does rather than showing a long provider string. */
const MODEL_KEYS = new Set(["figure_judge_model", "text_response_judge_model"]);

/**
 * The judge configuration behind a "Judging config N" filter, so a reviewer can
 * read what it actually is. Collapsed by default — it is reference detail a
 * reader opens when they want it, not something to push the table down every
 * time a config is selected. Models are truncated the way the table truncates
 * them; rubric text shows in full on hover.
 */
export const RunConfigSummary = ({
  label,
  judgeConfig,
}: {
  label: string;
  judgeConfig: Record<string, string>;
}) => {
  const entries = Object.entries(judgeConfig);
  return (
    <Collapsible className="rounded-md border border-border bg-muted/40">
      <CollapsibleTrigger className="group flex w-full cursor-pointer items-center gap-1.5 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
        <ChevronDown
          className="size-3.5 transition-transform group-data-[state=open]:rotate-180"
          aria-hidden
        />
        {label}
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-2">
        {entries.length === 0 ? (
          <p className="text-xs text-muted-foreground">No judge configuration recorded.</p>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1">
            {entries.map(([key, value]) => (
              <div key={key} className="col-span-2 grid grid-cols-subgrid items-baseline">
                <dt className="text-xs font-medium text-muted-foreground">
                  {CONFIG_KEY_LABELS[key] ?? key}
                </dt>
                <dd className="min-w-0 text-xs text-foreground">
                  <span
                    className={cn(MODEL_KEYS.has(key) ? "font-mono" : "block truncate")}
                    title={value}
                  >
                    {MODEL_KEYS.has(key) ? shortModelName(value) : value}
                  </span>
                </dd>
              </div>
            ))}
          </dl>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};
